import { BulbOutlined, RightOutlined, SearchOutlined, StarOutlined } from "@ant-design/icons";
import { Button, Empty, Flex, List, Modal, Spin, Typography } from "antd";
import Input from "antd/es/input/Input";
import { useEffect, useMemo, useState } from "react";
import { i18n } from "src/utils";
import AiAgentApi from "./api";
import { fullWidth } from "./styles";
import { type AiAgentSavedPrompt } from "./types";

const { Title, Text } = Typography;

interface PromptsModalProps {
  open: "favorites" | "recommendations" | null;
  cancel: () => void;
  favoritePrompts: AiAgentSavedPrompt[];
  setInput: React.Dispatch<React.SetStateAction<string>>;
  token: string;
  isAdmin: boolean;
}

export default function PromptsModal({ open, cancel, favoritePrompts, setInput, token, isAdmin }: PromptsModalProps) {
  const isFavorites = open === "favorites";
  const isRecommendations = open === "recommendations";

  const [filter, setFilter] = useState("");

  const [recommendedPrompts, setRecommendedPrompts] = useState<AiAgentSavedPrompt[]>([]);
  const [recommendedPromptsAccess, setRecommendedPromptsAccess] = useState(true);
  const [loadingRecommendedPrompts, setLoadingRecommendedPrompts] = useState(false);

  const prompts = isFavorites ? favoritePrompts : recommendedPrompts;

  const filteredPrompts = useMemo(() => {
    if (!isRecommendations || !filter) return prompts;
    return prompts.filter(
      (prompt) =>
        prompt.title?.toLowerCase().includes(filter.toLowerCase()) ||
        prompt.category?.toLowerCase().includes(filter.toLowerCase()),
    );
  }, [filter, prompts]);

  useEffect(() => {
    if (isRecommendations) getRecommendedPromptsForUsers();
  }, [open]);

  async function getRecommendedPromptsForUsers() {
    setLoadingRecommendedPrompts(true);
    const { list, access } = await new AiAgentApi(token).getRecommendedPromptsForUsers();
    setRecommendedPrompts(list);
    setRecommendedPromptsAccess(access);
    setLoadingRecommendedPrompts(false);
  }

  function handleSelect(content: string) {
    if (isAdmin) return;
    setInput(content);
    cancel();
  }

  return (
    <Modal
      open={!!open}
      title={
        <Title
          style={{ color: "#884EA6" }}
          level={4}
        >
          {isFavorites ? <StarOutlined /> : <BulbOutlined />}
          &nbsp;
          {i18n(`aiAgent.prompts.${open}`)}
        </Title>
      }
      onCancel={cancel}
      footer={false}
    >
      <Flex
        style={{ marginTop: 16 }}
        vertical
        gap={8}
      >
        {isRecommendations && (
          <Input
            value={filter}
            onChange={({ target }) => {
              setFilter(target.value);
            }}
            placeholder={i18n("aiAgent.prompts.recommendationsSearchPlaceholder")}
            suffix={<SearchOutlined />}
          />
        )}
        {loadingRecommendedPrompts ? (
          <Spin
            spinning
            style={{ marginTop: 16 }}
          />
        ) : !loadingRecommendedPrompts && filteredPrompts.length ? (
          <List
            style={{ ...fullWidth }}
            dataSource={filteredPrompts}
            renderItem={({ content, title, category }) => (
              <List.Item
                actions={[
                  <Button
                    size="small"
                    shape="circle"
                    type="text"
                    icon={<RightOutlined style={{ color: "#884EA6" }} />}
                    onClick={() => {
                      handleSelect(content);
                    }}
                    disabled={isAdmin}
                  />,
                ]}
              >
                <List.Item.Meta
                  title={
                    <a
                      onClick={() => {
                        handleSelect(content);
                      }}
                    >
                      {isRecommendations ? title : content}
                    </a>
                  }
                  description={isRecommendations ? category : ""}
                />
              </List.Item>
            )}
            pagination={
              filteredPrompts.length
                ? {
                    align: "center",
                    pageSize: 5,
                  }
                : false
            }
          />
        ) : isRecommendations && !recommendedPromptsAccess ? (
          <Text>{i18n("aiAgent.prompts.recommendationsNoAccess")}</Text>
        ) : (
          <Empty style={{ marginTop: 16 }} />
        )}
      </Flex>
    </Modal>
  );
}
